import React, { useState, useMemo, useEffect, useCallback, useRef, useContext } from 'react';
import {
  UnifiedCalendarItem,
  CalendarFilterState,
  CalendarView,
  CALENDAR_ITEM_COLORS,
  CALENDAR_ITEM_ICONS,
  toLocalDateString,
} from '../../../types/calendar';
import EventPopover from './EventPopover';
import CalendarFilters from './CalendarFilters';
import DayDetailPanel, { DayItemEntry } from './DayDetailPanel';
import CalendarContext from './CalendarContext';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';
import './Calendar.scss';

/**
 * Per-layer freshness, so an empty month can be told apart from a broken
 * accounting connection. Every field is optional: this is read off the calendar
 * context, and a missing field must degrade to "loaded and connected" rather
 * than crash the grid.
 */
export interface CalendarLayerStatus {
  eventsLoading?: boolean;
  invoicesLoading?: boolean;
  repeatingInvoicesLoading?: boolean;
  jobsLoading?: boolean;
  /** Xero/accounting link. When false, the invoice and bill layers are incomplete. */
  xeroConnected?: boolean;
}

interface FirebaseCalendarComponentProps {
  supportOnly?: boolean;
  calendarItems: UnifiedCalendarItem[];
  onItemClick: (item: UnifiedCalendarItem) => void;
  onDatesSet?: (start: Date, end: Date) => void;
  /** Optional date lets a day-level affordance (the detail panel) pre-seed the new event's date. */
  onNewEvent?: (date?: Date) => void;
  /** The canonical filter shape — mirroring it inline is how the two drifted apart before. */
  filters?: CalendarFilterState;
  onFilterChange?: (filters: Partial<CalendarFilterState>) => void;
  /** Overrides the status read off CalendarContext. Only needed outside the provider. */
  layerStatus?: CalendarLayerStatus;

  // ── View + anchor date (W3-1 / W3-2) ────────────────────────────────────
  // Both are OPTIONAL and both fall back to internal defaults, because the
  // calendar still has one uncontrolled host (CalendarModal) alongside the new
  // `/calendar` route. A page that drives them from the URL passes all four;
  // the modal passes none and behaves exactly as it always has.
  /** Which layout to render. Defaults to 'month'. */
  view?: CalendarView;
  /** Fired when the user picks a different view from the toolbar switcher. */
  onViewChange?: (view: CalendarView) => void;
  /**
   * The day the calendar should be anchored on — `?date=YYYY-MM-DD` from the
   * route. Read as a LOCAL day, never `new Date('2026-08-03')` (UTC midnight,
   * i.e. the previous day anywhere west of Greenwich; Adelaide's +9:30 makes it
   * the same day, but the parse is wrong on principle and travels).
   *
   * It seeds the anchor and re-seeds it whenever the caller changes it, but the
   * anchor is NOT fully controlled: navigating with the chevrons must work in
   * the modal, which has no state to hand back.
   */
  date?: string | Date;
  /** Fired when toolbar navigation moves the anchor, so a route can keep `?date=` in step. */
  onDateChange?: (date: Date) => void;
}

interface CalendarDay {
  date: Date;
  key: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  items: DayItemEntry[];
}

// Shared empty result so days with nothing on them don't hand a fresh array to every consumer.
const NO_ITEMS: DayItemEntry[] = [];

/**
 * Hard ceiling on how many days one item may occupy.
 *
 * Multi-day bucketing walks a cursor from an item's start day to its end day, and
 * the end date is user- or Xero-supplied. A typo of 2206 for 2026 would otherwise
 * spin ~65,000 iterations and paint a chip on every visible cell forever. A year is
 * far beyond any real install and far beyond the 42 days a month view can show.
 */
const MAX_ITEM_SPAN_DAYS = 366;

/**
 * Chips were `<div onClick>`: invisible to assistive tech and unreachable by keyboard.
 * As `<button>`s they inherit the UA's Arial, centred text and button chrome, so those
 * three are reset — background, padding, radius and colour stay with the type class so
 * the chip renders exactly as it did. `fontFamily` (not the `font` shorthand) because an
 * inline shorthand would also override the class's font-size.
 */
const CHIP_BUTTON_RESET: React.CSSProperties = {
  appearance: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  lineHeight: 'inherit',
};

/**
 * Parse a calendar item's stored date into a LOCAL Date.
 *
 * All-day items store 'YYYY-MM-DD' and timed items store a full ISO instant, and
 * `new Date('2026-08-03')` is UTC midnight — a different calendar day anywhere west
 * of Greenwich. Date-only values are therefore built as local midnight explicitly,
 * which is also what CalendarContext does when it sorts, so bucketing and sorting
 * can never disagree about which day an item belongs to.
 */
function parseItemDate(value: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  return new Date(value);
}

/**
 * Whole days between two 'YYYY-MM-DD' strings (b - a), measured at UTC noon so
 * neither end of a DST transition can round the difference off by one. Adelaide's
 * half-hour offset makes naive millisecond division genuinely unsafe here.
 */
function dayDiff(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime();
  return Math.round(ms / 86400000);
}

/** A new Date `n` days from `date`, local-time (DST-safe, unlike +n*86400000). */
function addLocalDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Local midnight on the same calendar day. */
function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The MONDAY that starts `date`'s week — the grid has always been Monday-first,
 * so the week view has to agree with it or the same day would sit in different
 * columns depending on which view you were in.
 */
function startOfLocalWeek(date: Date): Date {
  const dow = date.getDay();
  return addLocalDays(startOfLocalDay(date), -(dow === 0 ? 6 : dow - 1));
}

/**
 * A `?date=` value (or a Date) -> a LOCAL Date, or null when unusable.
 * Same rule as parseItemDate: a bare 'YYYY-MM-DD' is built as local midnight,
 * never handed to `new Date()` (which reads it as UTC).
 */
function coerceAnchorDate(value: string | Date | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * How many days the agenda covers. Two weeks is the horizon a two-person trades
 * business actually plans over — far enough to see next week's bookings, near
 * enough that the list stays scannable on a phone.
 */
const AGENDA_DAYS = 14;

// Module scope, not component scope: they are static, and the toolbar heading
// memo (declared above the render) reads MONTH_NAMES — a component-level const
// would be in its temporal dead zone there.
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const VIEW_OPTIONS: { key: CalendarView; label: string }[] = [
  { key: 'month', label: 'Month' },
  { key: 'week', label: 'Week' },
  { key: 'agenda', label: 'Agenda' },
];

/** Money as it appears in a list row — whole dollars, en-AU, never truncated. */
function formatFullAmount(amount: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency || 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** '8:00 am' in the business's locale. Timed items only. */
function formatClock(value: string): string {
  const d = parseItemDate(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const FirebaseCalendarComponent: React.FC<FirebaseCalendarComponentProps> = ({
  calendarItems,
  onItemClick,
  onDatesSet,
  onNewEvent,
  filters,
  onFilterChange,
  layerStatus,
  view = 'month',
  onViewChange,
  date,
  onDateChange,
  supportOnly = false,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(() => coerceAnchorDate(date) ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hoveredItem, setHoveredItem] = useState<UnifiedCalendarItem | null>(null);
  const [hoverAnchorRect, setHoverAnchorRect] = useState<DOMRect | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(() => (typeof window === 'undefined' ? 1200 : window.innerWidth));
  // Measured height of ONE month row, so "+N more" can reflect what actually
  // fits rather than a guess from viewport width. Null until first measurement.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================
  // Per-layer status
  // ============================================
  // `invoicesLoading`, `repeatingInvoicesLoading` and `xeroConnected` have existed on
  // the calendar context with ZERO consumers, which is why a month with no invoices
  // looked exactly like a month whose accounting connection had dropped. Read
  // straight off the context (this component only ever renders inside the provider)
  // via useContext rather than the useCalendar() hook, so it stays renderable
  // outside one. Each field is copied explicitly: if the provider ever renames one,
  // this is a compile error rather than a silently permanently-loaded pill.
  const calendarCtx = useContext(CalendarContext);
  const status: CalendarLayerStatus = useMemo(() => {
    if (layerStatus) return layerStatus;
    // `jobsLoading` is not on the context type yet — the jobs layer is landing alongside
    // this change. Read it tolerantly so the Jobs pill works either way; the other four
    // are read as typed properties precisely so a rename breaks the build.
    const loose = calendarCtx as unknown as Record<string, unknown> | null;
    return {
      eventsLoading: calendarCtx?.eventsLoading,
      invoicesLoading: calendarCtx?.invoicesLoading,
      repeatingInvoicesLoading: calendarCtx?.repeatingInvoicesLoading,
      jobsLoading: typeof loose?.jobsLoading === 'boolean' ? loose.jobsLoading : undefined,
      xeroConnected: calendarCtx?.xeroConnected,
    };
  }, [layerStatus, calendarCtx]);

  const anyLayerLoading = !!(
    status.eventsLoading ||
    status.invoicesLoading ||
    status.repeatingInvoicesLoading ||
    status.jobsLoading || calendarCtx?.commitmentsLoading
  );

  // `xeroConnected` starts false while the connection is still being established,
  // so waiting for the load to settle is what stops a warning flashing on every open.
  const accountingDisconnected = status.xeroConnected === false && !status.invoicesLoading;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayKey = useMemo(() => toLocalDateString(today), [today]);
  const tomorrowKey = useMemo(() => toLocalDateString(addLocalDays(today, 1)), [today]);

  // ============================================
  // Anchor date (URL <-> component)
  // ============================================
  // `onDateChange` is held in a ref so the navigation callbacks don't have to
  // list it as a dependency: a route that rebuilds the handler on every render
  // would otherwise re-create `navigate`/`goToToday` on every render too.
  const onDateChangeRef = useRef(onDateChange);
  useEffect(() => { onDateChangeRef.current = onDateChange; }, [onDateChange]);
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => { onViewChangeRef.current = onViewChange; }, [onViewChange]);

  // Re-seed from the caller's `date` when (and only when) the DAY it names
  // changes. Comparing day strings — not Date identity — is what stops a caller
  // that passes `new Date(...)` inline from resetting the user's navigation on
  // every single render.
  const anchorKey = useMemo(() => {
    const parsed = coerceAnchorDate(date);
    return parsed ? toLocalDateString(parsed) : null;
  }, [date]);
  const lastAnchorKeyRef = useRef<string | null>(anchorKey);
  useEffect(() => {
    if (!anchorKey || anchorKey === lastAnchorKeyRef.current) return;
    lastAnchorKeyRef.current = anchorKey;
    const parsed = coerceAnchorDate(anchorKey);
    if (parsed) setCurrentDate(parsed);
  }, [anchorKey]);

  /** Move the anchor AND tell the caller, so `?date=` can follow the chevrons. */
  const setAnchor = useCallback((next: Date) => {
    setCurrentDate(next);
    lastAnchorKeyRef.current = toLocalDateString(next);
    onDateChangeRef.current?.(next);
  }, []);

  // The view is seeded from the prop but held locally, so the calendar switches
  // views in BOTH hosts: the `/calendar` route drives it from `?view=` and gets
  // the change back through onViewChange, while CalendarModal passes neither and
  // still gets a working switcher. A purely controlled `view` would leave the
  // modal's switcher inert.
  const [internalView, setInternalView] = useState<CalendarView>(view);
  useEffect(() => { setInternalView(view); }, [view]);
  const activeView = internalView;
  const changeView = useCallback((next: CalendarView) => {
    setInternalView(next);
    onViewChangeRef.current?.(next);
  }, []);

  /** What the chevrons move by — used for their aria-labels. */
  const navUnitLabel = activeView === 'week' ? 'week' : activeView === 'agenda' ? 'fortnight' : 'month';

  // The Andrei door. Mail, jobs and Google Ads all expose him; the calendar had
  // no way to ask about the schedule at all. The date travels as a proper
  // `calendar` context handle AND is restated in the prefill, because the
  // message text is what reaches the model today.
  const { openAndreiChat } = useAndreiChat();
  const askAndreiAboutPeriod = useCallback(() => {
    const anchorDay = toLocalDateString(currentDate);
    const label = currentDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const prefill = activeView === 'month'
      ? `Summarise what's on my calendar for ${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}.`
      : activeView === 'week'
        ? `Summarise my week around ${label} (${anchorDay}) — what's on, and what needs preparing?`
        : `What's coming up over the next fortnight from ${label} (${anchorDay})?`;
    openAndreiChat({
      context: { type: 'calendar', id: anchorDay, date: anchorDay, label },
      prefill,
    });
  }, [openAndreiChat, currentDate, activeView]);

  // Notify parent of date range changes.
  //
  // Deliberately keyed on the anchor's MONTH for every view, not on the visible
  // window: previous-month-1st .. next-month-last already contains the current
  // week and the next 14 days from any day in the month, so switching month ->
  // week -> agenda never re-runs the (expensive) item merge in CalendarContext,
  // and the month view's range is byte-identical to what it always was.
  useEffect(() => {
    if (onDatesSet) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month + 2, 0);
      onDatesSet(startDate, endDate);
    }
  }, [currentDate, onDatesSet]);

  // Bucket every item into its local day ONCE, keyed YYYY-MM-DD. The month loop used to re-filter
  // the whole item list per cell, which cost 42xN Date parses on every render — including renders
  // triggered by unrelated Firestore writes elsewhere in the app.
  // Items also arrive in Firestore document order, so a 4pm could sit above an 8am and a truncated
  // cell could hide the EARLIEST item of the day; sort all-day first, then by start time.
  //
  // An item now lands in EVERY day from its start through its end inclusive. `item.end` had no
  // reader anywhere in the codebase, so a three-day install showed on day one and left days two
  // and three looking free — a double-booking waiting to happen. Continuation days are marked so
  // they can render as "this started earlier" rather than "this starts today".
  const itemsByDay = useMemo(() => {
    const buckets = new Map<string, { entry: DayItemEntry; startMs: number }[]>();

    calendarItems.forEach(item => {
      const startDate = parseItemDate(item.start);
      const startMs = startDate.getTime();
      // Unparseable dates never matched a day under the old toDateString() comparison either.
      if (Number.isNaN(startMs)) return;

      const startKey = toLocalDateString(startDate);

      // How many EXTRA days past the start day this item covers.
      let extraDays = 0;
      if (item.end) {
        const endDate = parseItemDate(item.end);
        if (!Number.isNaN(endDate.getTime())) {
          let endKey = toLocalDateString(endDate);
          // A timed item ending at exactly local midnight finishes the PREVIOUS day —
          // a 9am-to-midnight job is one day's work, not two.
          if (
            !item.allDay &&
            endDate.getHours() === 0 && endDate.getMinutes() === 0 &&
            endDate.getSeconds() === 0 && endDate.getMilliseconds() === 0
          ) {
            endKey = toLocalDateString(addLocalDays(endDate, -1));
          }
          const diff = dayDiff(startKey, endKey);
          // diff <= 0 covers both same-day items and a corrupt end BEFORE the start:
          // either way the item occupies its start day only, which is what it did before.
          if (diff > 0) extraDays = Math.min(diff, MAX_ITEM_SPAN_DAYS);
        }
      }

      const totalDays = extraDays + 1;
      for (let offset = 0; offset <= extraDays; offset++) {
        const key = offset === 0 ? startKey : toLocalDateString(addLocalDays(startDate, offset));
        const entry: DayItemEntry = {
          item,
          isStart: offset === 0,
          isEnd: offset === extraDays,
          dayIndex: offset + 1,
          totalDays,
        };
        const bucket = buckets.get(key);
        if (bucket) {
          bucket.push({ entry, startMs });
        } else {
          buckets.set(key, [{ entry, startMs }]);
        }
      }
    });

    const byDay = new Map<string, DayItemEntry[]>();
    buckets.forEach((bucket, key) => {
      // Array.prototype.sort is stable, so items at the same time keep their original order.
      // A multi-day item sorts by its ORIGINAL start on every day it covers, which floats an
      // already-running job to the top of the day — where "still going" belongs.
      bucket.sort((a, b) => {
        const allDayDiff = (a.entry.item.allDay ? 0 : 1) - (b.entry.item.allDay ? 0 : 1);
        if (allDayDiff !== 0) return allDayDiff;
        return a.startMs - b.startMs;
      });
      byDay.set(key, bucket.map(e => e.entry));
    });

    return byDay;
  }, [calendarItems]);

  /**
   * One day, resolved against the bucketed item map. Shared by all three views so
   * a day can never be described differently depending on which one you are in.
   * `monthRef` is the month the view considers "current" (only the month grid
   * greys anything out; week and agenda pass their own anchor month and simply
   * ignore the flag).
   */
  const buildDay = useCallback((date: Date, monthRef: number): CalendarDay => {
    const key = toLocalDateString(date);
    const dayOfWeekNum = date.getDay();
    return {
      date: new Date(date),
      key,
      isCurrentMonth: date.getMonth() === monthRef,
      isToday: key === todayKey,
      isWeekend: dayOfWeekNum === 0 || dayOfWeekNum === 6,
      items: itemsByDay.get(key) ?? NO_ITEMS,
    };
  }, [itemsByDay, todayKey]);

  // Generate calendar days for month view
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Start from Monday
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    // End on Sunday
    const endDate = new Date(lastDay);
    const endDayOfWeek = lastDay.getDay();
    const daysToAdd = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + daysToAdd);

    const days: CalendarDay[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(buildDay(current, month));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentDate, buildDay]);

  // ============================================
  // Week view (W3-2)
  // ============================================
  // A per-day, time-ordered COLUMN LIST rather than a positioned time grid.
  // Deliberate: a positioned grid has to invent a day start/end, decide what to
  // do with the six all-day layers this calendar merges (invoices, bills,
  // recurring templates, job due dates — none of which have a time at all), and
  // solve overlap geometry. A column list shows the same "what am I committed to
  // on Thursday, in order" answer, puts the all-day money items where they
  // belong (at the top of the day, which is how the buckets already sort), and
  // degrades to a stack on a phone.
  const weekDays = useMemo(() => {
    const start = startOfLocalWeek(currentDate);
    const month = currentDate.getMonth();
    return Array.from({ length: 7 }, (_, i) => buildDay(addLocalDays(start, i), month));
  }, [currentDate, buildDay]);

  // ============================================
  // Agenda view (W3-2)
  // ============================================
  const agendaDays = useMemo(() => {
    const start = startOfLocalDay(currentDate);
    const month = currentDate.getMonth();
    return Array.from({ length: AGENDA_DAYS }, (_, i) => buildDay(addLocalDays(start, i), month));
  }, [currentDate, buildDay]);

  // Empty days are dropped so the list stays scannable on a phone — 14 headers
  // reading "nothing scheduled" is noise. Today and tomorrow are the exception:
  // "am I free this afternoon / tomorrow" is the question this view exists to
  // answer, and silence is not an answer to it.
  const agendaVisibleDays = useMemo(
    () => agendaDays.filter(d => d.items.length > 0 || d.key === todayKey || d.key === tomorrowKey),
    [agendaDays, todayKey, tomorrowKey]
  );

  /**
   * The toolbar heading. "August 2026" is wrong for a week that straddles two
   * months and actively misleading for the agenda, which starts from the anchor
   * day rather than the 1st.
   */
  const headingText = useMemo(() => {
    if (activeView === 'month') {
      return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
    const span = activeView === 'week' ? weekDays : agendaDays;
    const first = span[0]?.date;
    const last = span[span.length - 1]?.date;
    if (!first || !last) return `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const sameYear = first.getFullYear() === last.getFullYear();
    const sameMonth = sameYear && first.getMonth() === last.getMonth();
    const left = first.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: sameMonth ? undefined : 'short',
      year: sameYear ? undefined : 'numeric',
    });
    const right = last.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${left} – ${right}`;
  }, [activeView, currentDate, weekDays, agendaDays]);

  // The 42 cells are direct children of a 7-column CSS grid, so the week wrappers that
  // give the grid its ARIA rows must be `display: contents` — see the render.
  const calendarWeeks = useMemo(() => {
    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    return weeks;
  }, [calendarDays]);

  const dayKeySet = useMemo(() => new Set(calendarDays.map(d => d.key)), [calendarDays]);

  // How many item-days the rendered window holds. Zero (with every layer settled) is a
  // genuinely empty month and is worth saying out loud.
  const visibleItemCount = useMemo(
    () => calendarDays.reduce((sum, d) => sum + d.items.length, 0),
    [calendarDays]
  );

  // Navigation.
  //
  // The step follows the VIEW, not the month. Paging a week view by a whole
  // month skips three weeks the user never sees, and paging the agenda by a
  // month leaves a gap between where the list ended and where it resumes.
  const navigate = useCallback((direction: 'prev' | 'next') => {
    const sign = direction === 'next' ? 1 : -1;
    setAnchor((() => {
      if (activeView === 'week') return addLocalDays(currentDate, sign * 7);
      if (activeView === 'agenda') return addLocalDays(currentDate, sign * AGENDA_DAYS);
      const next = new Date(currentDate);
      // Clamp to the target month's length: setMonth on the 31st rolls FORWARD
      // into the next month (31 Jan -> 3 Mar), so paging from a long month
      // could skip February entirely. Same class of bug as the recurrence
      // rollover fixed in src/app/utils/recurrence.ts.
      next.setDate(1);
      next.setMonth(currentDate.getMonth() + sign);
      const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
      next.setDate(Math.min(currentDate.getDate(), lastDay));
      return next;
    })());
  }, [activeView, currentDate, setAnchor]);

  const goToToday = useCallback(() => {
    setAnchor(new Date());
  }, [setAnchor]);

  // ============================================
  // Keyboard navigation (roving tabindex)
  // ============================================
  // The grid was 42 plain divs with onClick and no roles: unreachable by keyboard and
  // announced as nothing. One cell is tabbable at a time and the arrows move between
  // them, which is the standard grid pattern and keeps the Tab stop count at one.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const focusPendingRef = useRef(false);
  const dayRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // The remembered cell may have scrolled out of the window (month change, or first
  // render). Fall back to today when it is on screen, otherwise the 1st of the month —
  // always present in a Monday-aligned six-week window.
  const activeKey = useMemo(() => {
    if (focusedKey && dayKeySet.has(focusedKey)) return focusedKey;
    const todayKey = toLocalDateString(today);
    if (dayKeySet.has(todayKey)) return todayKey;
    return toLocalDateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  }, [focusedKey, dayKeySet, today, currentDate]);

  // Only ever pull focus in response to a key the user actually pressed — never on mount,
  // and never because an unrelated Firestore write re-rendered the month.
  useEffect(() => {
    if (!focusPendingRef.current) return;
    focusPendingRef.current = false;
    dayRefs.current.get(activeKey)?.focus();
  }, [activeKey]);

  const moveFocusTo = useCallback((target: Date, forceMonth = false) => {
    const key = toLocalDateString(target);
    // Landing on the cell you are already standing on (Home while on a Monday) must not
    // arm the flag: nothing re-renders, so it would sit set and then steal focus on some
    // later, unrelated move.
    if (key !== activeKey) focusPendingRef.current = true;
    setFocusedKey(key);
    // Arrowing onto a trailing/leading cell that is already on screen keeps the month;
    // stepping off the window (or PageUp/PageDown) follows the date into its own month.
    if (forceMonth || !dayKeySet.has(key)) {
      setCurrentDate(prev =>
        prev.getFullYear() === target.getFullYear() && prev.getMonth() === target.getMonth()
          ? prev // same ref: don't churn the onDatesSet effect
          : new Date(target.getFullYear(), target.getMonth(), 1)
      );
    }
  }, [dayKeySet, activeKey]);

  // Day selection
  const handleDayClick = useCallback((day: CalendarDay) => {
    setSelectedDate(day.date);
    setIsPanelOpen(true);
    // Keep the tab stop where the user last acted.
    setFocusedKey(day.key);
  }, []);

  const handleDayKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>, day: CalendarDay) => {
    const d = day.date;
    let target: Date | null = null;
    let forceMonth = false;

    switch (e.key) {
      case 'ArrowLeft':
        target = addLocalDays(d, -1);
        break;
      case 'ArrowRight':
        target = addLocalDays(d, 1);
        break;
      case 'ArrowUp':
        target = addLocalDays(d, -7);
        break;
      case 'ArrowDown':
        target = addLocalDays(d, 7);
        break;
      case 'Home': {
        // Weeks start Monday in this grid, so Sunday is 6 days into its week.
        const dow = d.getDay();
        target = addLocalDays(d, -(dow === 0 ? 6 : dow - 1));
        break;
      }
      case 'End': {
        const dow = d.getDay();
        target = addLocalDays(d, dow === 0 ? 0 : 7 - dow);
        break;
      }
      case 'PageUp':
      case 'PageDown': {
        const delta = e.key === 'PageUp' ? -1 : 1;
        const month = d.getMonth() + delta;
        // Clamp: the 31st of a month has no counterpart in the next one.
        const lastOfTarget = new Date(d.getFullYear(), month + 1, 0).getDate();
        target = new Date(d.getFullYear(), month, Math.min(d.getDate(), lastOfTarget));
        forceMonth = true; // changing month IS the point of these two keys
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDayClick(day);
        return;
      default:
        return;
    }

    e.preventDefault();
    moveFocusTo(target, forceMonth);
  }, [handleDayClick, moveFocusTo]);

  // Hover handling
  const handleItemMouseEnter = useCallback((item: UnifiedCalendarItem, event: React.MouseEvent) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Measure the chip NOW: `event.target` inside the timeout can be the inner icon or
    // title span, which anchors the popover to a few pixels instead of the whole chip.
    const rect = event.currentTarget.getBoundingClientRect();
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverAnchorRect(rect);
      setHoveredItem(item);
    }, 400); // Delay before showing popover
  }, []);

  const handleItemMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredItem(null);
    setHoverAnchorRect(null);
  }, []);

  // Get items for selected day (same bucketed + span-aware list the grid cell renders)
  const selectedDayItems = useMemo(() => {
    if (!selectedDate) return NO_ITEMS;
    return itemsByDay.get(toLocalDateString(selectedDate)) ?? NO_ITEMS;
  }, [selectedDate, itemsByDay]);

  // Counts for filter pills. Deliberately counted over the SOURCE items, not the
  // day buckets: a three-day install is one thing on the calendar, not three.
  const counts = useMemo(() => ({
    events: calendarItems.filter(i => i.type === 'event').length,
    invoices: calendarItems.filter(i => i.type === 'invoice_due' || i.type === 'invoice_created').length,
    recurring: calendarItems.filter(i => i.type === 'recurring_invoice' || i.type === 'recurring_expense').length,
    expenses: calendarItems.filter(i => i.type === 'bill_due').length,
    jobs: calendarItems.filter(i => i.type === 'job_due').length,
    support: calendarItems.filter(i => i.commitment?.kind === 'support').length,
    stripe: calendarItems.filter(i => i.commitment?.kind === 'stripe').length,
  }), [calendarItems]);

  // Track viewport width for density tuning (month view)
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // How many chips a cell shows before collapsing the rest into "+N more".
  //
  // This used to be 8/10/12/14 — numbers no cell has ever been able to display. A day cell
  // is ~100px tall with a ~32px header, and a chip is ~25px, so three is the honest figure
  // on a laptop and one or two on a phone. The overflow did not even scroll discoverably:
  // Calendar.scss zeroes the scrollbar, so items 4-14 were simply invisible with no "+N more"
  // to reveal them. Erring low costs a click; erring high hides money.
  // Track the rendered row height. A ResizeObserver rather than a window resize
  // listener because the grid also changes height when the toolbar/filter strip
  // wraps, when the day panel opens, and when the browser zoom changes — none of
  // which necessarily fire a window resize.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const h = el.clientHeight;
      if (h > 0) setRowHeight(h / 6);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeView]);

  // Width-only fallback, used until the grid has been measured (and on phones,
  // where the rows size to their content rather than sharing a fixed height).
  const widthBasedCap = useMemo(() => {
    if (viewportWidth >= 1600) return 6;
    if (viewportWidth >= 1200) return 5;
    if (viewportWidth >= 992) return 4;
    if (viewportWidth >= 768) return 4;
    return 3; // phone
  }, [viewportWidth]);

  // On desktop the six rows now SHARE the available height (Calendar.scss:
  // `grid-template-rows: repeat(6, minmax(96px, 1fr))`), so how many chips fit
  // is a function of window height, not width — a short laptop window and a tall
  // one are the same width and fit very different amounts. Guessing from width
  // is what left items 4-14 invisible behind a zeroed scrollbar.
  const maxRenderItemsInCell = useMemo(() => {
    if (rowHeight === null || viewportWidth < 768) return widthBasedCap;
    const DAY_HEADER_H = 26;   // day number + count badge row
    const CELL_PADDING = 10;   // 4px top + 4px bottom + a little breathing room
    const CHIP_H = 25;         // chip height + 3px gap
    const usable = rowHeight - DAY_HEADER_H - CELL_PADDING;
    const fits = Math.floor(usable / CHIP_H);
    // At least one, and never more than the width-based ceiling (a very tall
    // window shouldn't render twenty chips into one cell).
    return Math.max(1, Math.min(widthBasedCap, fits));
  }, [rowHeight, viewportWidth, widthBasedCap]);

  const dayNames = DAY_NAMES;

  const getItemTypeClass = (type: string) => {
    switch (type) {
      case 'event': return 'modern-calendar__event--event';
      case 'invoice_due': return 'modern-calendar__event--invoice';
      case 'invoice_created': return 'modern-calendar__event--created';
      case 'recurring_invoice': return 'modern-calendar__event--recurring';
      case 'recurring_expense': return 'modern-calendar__event--expense';
      case 'bill_due': return 'modern-calendar__event--bill';
      case 'job_due': return 'modern-calendar__event--job';
      default: return '';
    }
  };

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'event': return 'bi-calendar-event';
      case 'invoice_due': return 'bi-receipt';
      case 'invoice_created': return 'bi-file-earmark-plus';
      case 'recurring_invoice':
      case 'recurring_expense': return 'bi-arrow-repeat';
      case 'bill_due': return 'bi-receipt';
      case 'job_due': return CALENDAR_ITEM_ICONS.job_due;
      default: return 'bi-calendar';
    }
  };

  const isTemplateItem = (type: string) =>
    type === 'recurring_invoice' || type === 'recurring_expense';

  const formatCompactTitle = (item: UnifiedCalendarItem) => {
    if (item.commitment) return item.title;
    if (item.type === 'event') {
      return item.eventData?.title || item.title;
    }
    if (item.type === 'invoice_due') {
      return item.customerName || item.title;
    }
    if (item.type === 'recurring_invoice') {
      return item.customerName || 'Recurring';
    }
    if (item.type === 'job_due') {
      return item.jobData?.jobLabel || item.title;
    }
    return item.title;
  };

  const formatCompactAmount = (item: UnifiedCalendarItem) => {
    // For recurring templates and bills/invoices, always show the dollar value (template or document total)
    if (item.amount !== undefined && item.amount !== null) {
      if (item.commitment?.kind === 'stripe') return new Intl.NumberFormat('en-AU', { style: 'currency', currency: item.currencyCode || 'AUD' }).format(item.amount);
      return `$${Math.round(item.amount)}`;
    }
    return null;
  };

  /**
   * Chip styling. `--job` has no stylesheet rule yet (the SCSS is another owner's file
   * this wave), so jobs take their colour from the item the way events always have.
   * Continuation days lose the rounding on the edge they continue across, which is what
   * makes a span read as one bar running through the week instead of three separate jobs.
   */
  const chipStyle = (entry: DayItemEntry): React.CSSProperties | undefined => {
    const { item, isStart, isEnd } = entry;
    const needsOwnColour = item.type === 'event' || item.type === 'job_due';
    if (!needsOwnColour && isStart && isEnd) return CHIP_BUTTON_RESET;

    const style: React.CSSProperties = { ...CHIP_BUTTON_RESET };
    if (needsOwnColour) {
      style.background = item.color || (item.type === 'job_due' ? CALENDAR_ITEM_COLORS.job_due : CALENDAR_ITEM_COLORS.event);
    }
    if (!isStart) {
      // Squared-off edge only. Deliberately NOT an inline opacity: `--template`'s
      // `:hover { opacity: 1 }` proves the class layer owns that dimension, and an
      // inline value would win permanently and kill the hover. The stronger signal
      // is the return-arrow icon that replaces the type icon on continuation days.
      style.borderTopLeftRadius = 0;
      style.borderBottomLeftRadius = 0;
    }
    if (!isEnd) {
      style.borderTopRightRadius = 0;
      style.borderBottomRightRadius = 0;
    }
    return style;
  };

  const chipLabel = (entry: DayItemEntry): string => {
    if (entry.totalDays <= 1) return entry.item.title;
    const position = `day ${entry.dayIndex} of ${entry.totalDays}`;
    return entry.isStart ? `${entry.item.title} (${position})` : `${entry.item.title} (continues, ${position})`;
  };

  const someFiltersOff = !!filters && !(
    filters.showEvents &&
    filters.showInvoices &&
    filters.showRecurringInvoices &&
    filters.showRecurringExpenses &&
    filters.showJobs && filters.showSupport !== false && filters.showStripePayments !== false
  );

  /**
   * The time shown on a week/agenda row.
   *
   * Six of the seven layers are all-day by construction (invoices, bills,
   * recurring occurrences, job due dates) — printing "12:00am" against a bill
   * would invent a precision the data does not have, so they get a dash.
   */
  const rowTime = (entry: DayItemEntry): string => {
    const item = entry.item;
    if (!entry.isStart) return '→';
    if (item.allDay || !item.start.includes('T')) return '—';
    const d = new Date(item.start);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase().replace(' ', '');
  };

  /**
   * One item as a full-width row. Week and agenda both use this: unlike the
   * month's compact chip there is horizontal room here for the time, the full
   * title and the amount, which is the whole reason these views exist.
   */
  const renderItemRow = (entry: DayItemEntry, keyPrefix: string) => (
    <button
      type="button"
      key={`${keyPrefix}-${entry.item.id}-${entry.dayIndex}`}
      className={`modern-calendar__row ${getItemTypeClass(entry.item.type)}${isTemplateItem(entry.item.type) ? ' modern-calendar__event--template' : ''}${entry.isStart ? '' : ' modern-calendar__event--continuation'}`}
      style={chipStyle(entry)}
      onClick={() => onItemClick(entry.item)}
      onMouseEnter={(e) => handleItemMouseEnter(entry.item, e)}
      onMouseLeave={handleItemMouseLeave}
      title={chipLabel(entry)}
      aria-label={`${rowTime(entry) === '—' ? 'All day' : rowTime(entry)}, ${chipLabel(entry)}`}
    >
      <span className="modern-calendar__row-time">{rowTime(entry)}</span>
      <i className={`bi ${entry.isStart ? (entry.item.icon || getItemIcon(entry.item.type)) : 'bi-arrow-return-right'}`} aria-hidden="true"></i>
      <span className="modern-calendar__row-title">{formatCompactTitle(entry.item)}</span>
      {entry.isStart && formatCompactAmount(entry.item) && (
        <span className="modern-calendar__row-amount">{formatCompactAmount(entry.item)}</span>
      )}
    </button>
  );

  return (
    <div className="modern-calendar">
      {/* Toolbar */}
      <div className="modern-calendar__toolbar">
        <div className="modern-calendar__toolbar-left">
          <h2 className="modern-calendar__title">{headingText}</h2>
          <button className="modern-calendar__today-btn" onClick={goToToday}>
            Today
          </button>

          {/* View switcher. The styles for this have existed in Calendar.scss
              since before the switcher did — nothing ever rendered it. */}
          <div className="modern-calendar__view-toggle" role="group" aria-label="Calendar view">
            {VIEW_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`modern-calendar__view-btn${activeView === opt.key ? ' modern-calendar__view-btn--active' : ''}`}
                aria-pressed={activeView === opt.key}
                onClick={() => changeView(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="modern-calendar__toolbar-right">
          <button
            className="modern-calendar__today-btn"
            onClick={askAndreiAboutPeriod}
            title="Ask Andrei about what's on"
          >
            <i className="bi bi-stars me-1"></i>
            Ask Andrei
          </button>
          {onNewEvent && (
            /* Month-level affordance — no date, so the form falls back to today */
            <button className="modern-calendar__today-btn" onClick={() => onNewEvent()}>
              <i className="bi bi-plus-lg me-1"></i>
              New Event
            </button>
          )}
          <button className="modern-calendar__nav-btn" onClick={() => navigate('prev')} aria-label={`Previous ${navUnitLabel}`}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="modern-calendar__nav-btn" onClick={() => navigate('next')} aria-label={`Next ${navUnitLabel}`}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>

      {filters && onFilterChange && <CalendarFilters
        filters={filters} onChange={onFilterChange} supportOnly={supportOnly}
        counts={{ ...counts, jobs: counts.jobs - counts.support, recurring: counts.recurring - counts.stripe }}
        loading={{ events: status.eventsLoading, jobs: status.jobsLoading, invoices: status.invoicesLoading,
          expenses: status.invoicesLoading, recurring: status.repeatingInvoicesLoading,
          support: calendarCtx?.commitmentsLoading, stripe: calendarCtx?.commitmentsLoading }}
      />}
      {!!calendarCtx?.commitmentNotices.length && <details className="calendar-commitment-notice" role="status">
        <summary>{calendarCtx.commitmentNotices.length} support or payment sources need review</summary>
        <ul>{calendarCtx.commitmentNotices.map((notice, index) => <li key={index}>{notice}</li>)}</ul>
      </details>}

      {/* Accounting connection notice. Deliberately quiet — the calendar still works,
          it just cannot promise the invoice and bill layers are complete. */}
      {accountingDisconnected && (
        <div
          className="modern-calendar__notice modern-calendar__notice--warning"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 20px',
            fontSize: '0.8125rem',
            color: 'var(--bs-warning-text-emphasis, var(--bs-body-color))',
            background: 'rgba(var(--bs-warning-rgb, 255, 193, 7), 0.12)',
            borderBottom: '1px solid var(--bs-border-color)',
          }}
        >
          <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
          <span>Accounting isn’t connected — invoices and bills may be missing or out of date.</span>
        </div>
      )}

      {/* Grid Wrapper with Panel */}
      <div className="modern-calendar__grid-wrapper">
        {/* Calendar Grid — `position: relative` anchors the empty-month message below. */}
        <div className="modern-calendar__grid" style={{ position: 'relative' }}>
          {activeView === 'month' && (<>
          {/* Day Headers */}
          <div className="modern-calendar__header">
            {dayNames.map((day, index) => (
              <div
                key={day}
                className={`modern-calendar__header-cell ${index >= 5 ? 'modern-calendar__header-cell--weekend' : ''}`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Body */}
          <div
            ref={bodyRef}
            className="modern-calendar__body"
            role="grid"
            aria-label={`${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            aria-rowcount={calendarWeeks.length}
            aria-colcount={7}
          >
            {calendarWeeks.map((week, weekIndex) => (
              // An ARIA grid needs rows, but the 7-column CSS grid needs the day cells as
              // its DIRECT children. `display: contents` gives the row a place in the
              // accessibility tree while leaving the cells in the grid — the alternative
              // (a real row box) would collapse the month into six full-width strips.
              <div
                key={weekIndex}
                role="row"
                aria-rowindex={weekIndex + 1}
                style={{ display: 'contents' }}
              >
                {week.map((day, dayIndex) => {
                  // `maxRenderItemsInCell` is now what genuinely FITS the row (measured,
                  // not guessed), and the cell can no longer grow to swallow an extra —
                  // so the old "render one more rather than hide a single item" leniency
                  // would have pushed that extra chip out of sight instead of saving a
                  // click. When there are too many, the "+N more" row takes the last slot.
                  const visibleCount = day.items.length <= maxRenderItemsInCell
                    ? day.items.length
                    : Math.max(1, maxRenderItemsInCell - 1);
                  const hiddenCount = day.items.length - visibleCount;
                  const isFocusTarget = day.key === activeKey;
                  const isSelected = selectedDate?.toDateString() === day.date.toDateString();

                  return (
                    <div
                      key={day.key}
                      ref={(el) => {
                        if (el) dayRefs.current.set(day.key, el);
                        else dayRefs.current.delete(day.key);
                      }}
                      role="gridcell"
                      aria-colindex={dayIndex + 1}
                      aria-selected={isSelected}
                      // Roving tabindex: exactly one cell is in the tab order at a time.
                      tabIndex={isFocusTarget ? 0 : -1}
                      aria-label={`${day.date.toLocaleDateString('en-AU', {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                      })}${day.items.length ? `, ${day.items.length} item${day.items.length === 1 ? '' : 's'}` : ', no items'}`}
                      className={`modern-calendar__day
                        ${!day.isCurrentMonth ? 'modern-calendar__day--other-month' : ''}
                        ${day.isToday ? 'modern-calendar__day--today' : ''}
                        ${day.isWeekend ? 'modern-calendar__day--weekend' : ''}
                        ${isSelected ? 'modern-calendar__day--selected' : ''}`}
                      onClick={() => handleDayClick(day)}
                      onKeyDown={(e) => handleDayKeyDown(e, day)}
                    >
                      {/* Day Header */}
                      <div className="modern-calendar__day-header">
                        <span className="modern-calendar__day-number">{day.date.getDate()}</span>
                        {day.items.length > 0 && (
                          <span className="modern-calendar__day-count">{day.items.length}</span>
                        )}
                      </div>

                      {/* Events */}
                      <div className="modern-calendar__day-events">
                        {day.items.slice(0, visibleCount).map((entry) => (
                          <button
                            type="button"
                            // Not `item.id` alone: two occurrences of the same recurring
                            // event can both cover one day once spans exist (a 3-day event
                            // repeating daily), and until W2-2's per-occurrence ids are
                            // wired through they share a document id. dayIndex separates them.
                            key={`${entry.item.id}-${entry.dayIndex}`}
                            // Not in the tab order: a grid gives you one tab stop and the
                            // arrows. Enter on the cell opens the day panel, where every
                            // item is a real, tabbable button.
                            tabIndex={-1}
                            className={`modern-calendar__event ${getItemTypeClass(entry.item.type)}${isTemplateItem(entry.item.type) ? ' modern-calendar__event--template' : ''}${entry.isStart ? '' : ' modern-calendar__event--continuation'}`}
                            style={chipStyle(entry)}
                            onClick={(e) => {
                              e.stopPropagation();
                              onItemClick(entry.item);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => handleItemMouseEnter(entry.item, e)}
                            onMouseLeave={handleItemMouseLeave}
                            title={chipLabel(entry)}
                            aria-label={chipLabel(entry)}
                          >
                            <i
                              className={`bi ${entry.isStart ? (entry.item.icon || getItemIcon(entry.item.type)) : 'bi-arrow-return-right'} event-icon`}
                              aria-hidden="true"
                            ></i>
                            <span className="event-title">{formatCompactTitle(entry.item)}</span>
                            {entry.isStart && formatCompactAmount(entry.item) && (
                              <span className="event-amount">{formatCompactAmount(entry.item)}</span>
                            )}
                          </button>
                        ))}
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            tabIndex={-1}
                            className="modern-calendar__more-events"
                            // The stylesheet styles this as plain inline text, so strip the native button chrome.
                            // `fontFamily`, never the `font` shorthand — inline shorthand would beat the
                            // class's own font-size and blow this line up to body size.
                            style={{ background: 'none', border: 'none', textAlign: 'left', fontFamily: 'inherit', lineHeight: 'inherit' }}
                            onClick={(e) => {
                              // The day cell behind it opens the same panel — don't let the click fire twice.
                              e.stopPropagation();
                              handleDayClick(day);
                            }}
                          >
                            +{hiddenCount} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          </>)}

          {/* ── Week view ────────────────────────────────────────────────────
              Seven day columns, each a time-ordered list. Deliberately NOT a
              positioned time-grid: six of the seven layers this calendar merges
              are all-day (invoices, bills, recurring occurrences, job due
              dates), so a 24-hour canvas would be mostly empty with the money
              items stranded in a header band. A column list answers "what am I
              committed to on Thursday, in order" and collapses to a readable
              stack on a phone. */}
          {activeView === 'week' && (
            <div className="modern-calendar__week" role="list" aria-label={`Week of ${headingText}`}>
              {weekDays.map(day => (
                <div
                  key={day.key}
                  role="listitem"
                  className={`modern-calendar__week-day${day.isToday ? ' modern-calendar__week-day--today' : ''}${day.isWeekend ? ' modern-calendar__week-day--weekend' : ''}`}
                >
                  <button
                    type="button"
                    className="modern-calendar__week-day-header"
                    onClick={() => handleDayClick(day)}
                    aria-label={`Open ${day.date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                  >
                    <span className="modern-calendar__week-day-name">
                      {day.date.toLocaleDateString('en-AU', { weekday: 'short' })}
                    </span>
                    <span className="modern-calendar__day-number">{day.date.getDate()}</span>
                  </button>
                  <div className="modern-calendar__week-day-items">
                    {day.items.length === 0
                      ? <div className="modern-calendar__week-empty">—</div>
                      : day.items.map(entry => renderItemRow(entry, 'wk'))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Agenda view ──────────────────────────────────────────────────
              A flat, date-grouped list of the next fortnight. This is the view
              that actually works on a phone: no 7-column grid, no truncation,
              full titles. Empty days are dropped to keep it scannable, EXCEPT
              today and tomorrow — "am I free this afternoon" deserves an
              explicit answer, not silence. */}
          {activeView === 'agenda' && (
            <div className="modern-calendar__agenda">
              {agendaVisibleDays.length === 0 ? null : agendaVisibleDays.map(day => (
                <section key={day.key} className="modern-calendar__agenda-day">
                  <header className="modern-calendar__agenda-header">
                    <button
                      type="button"
                      className="modern-calendar__agenda-date"
                      onClick={() => handleDayClick(day)}
                    >
                      {day.key === todayKey
                        ? 'Today'
                        : day.key === tomorrowKey
                          ? 'Tomorrow'
                          : day.date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </button>
                    {day.items.length > 0 && (
                      <span className="modern-calendar__agenda-count">
                        {day.items.length} item{day.items.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </header>
                  {day.items.length === 0
                    ? <div className="modern-calendar__agenda-empty">Nothing scheduled.</div>
                    : day.items.map(entry => renderItemRow(entry, 'ag'))}
                </section>
              ))}
            </div>
          )}

          {/* Empty month. An empty grid alone reads as "the calendar is broken" just as
              easily as "nothing is scheduled"; say which. Non-interactive so the cells
              underneath stay clickable. */}
          {/* Month only: its copy names the month, and visibleItemCount counts the
              month grid. Week and agenda carry their own per-day empty states. */}
          {activeView === 'month' && !anyLayerLoading && visibleItemCount === 0 && (
            <div
              className="modern-calendar__empty-month"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                pointerEvents: 'none',
                textAlign: 'center',
                padding: 16,
                color: 'var(--bs-secondary-color)',
              }}
            >
              <i className="bi bi-calendar-check" style={{ fontSize: '2rem', opacity: 0.35 }} aria-hidden="true"></i>
              <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                Nothing scheduled in {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
              </div>
              <div style={{ fontSize: '0.8125rem' }}>
                {someFiltersOff
                  ? 'Some layers are switched off in the filters above.'
                  : accountingDisconnected
                    ? 'Invoices and bills are unavailable while accounting is disconnected.'
                    : 'Pick a day to add an event.'}
              </div>
            </div>
          )}
        </div>

        {/* Day Detail Panel */}
        <DayDetailPanel
          isOpen={isPanelOpen}
          selectedDate={selectedDate}
          items={selectedDayItems}
          onClose={() => {
            setIsPanelOpen(false);
            setSelectedDate(null);
          }}
          onItemClick={onItemClick}
          onAddEvent={onNewEvent || (() => {})}
        />
      </div>

      {/* Event Hover Popover */}
      {hoveredItem && hoverAnchorRect && (
        <EventPopover
          item={hoveredItem}
          anchorRect={hoverAnchorRect}
          onClose={() => {
            setHoveredItem(null);
            setHoverAnchorRect(null);
          }}
        />
      )}

      {/* The mobile FAB used to live here. Removed: it was `position: fixed` against the
          VIEWPORT rather than the modal, so it floated over the bottom-right day cells,
          sat inside the iOS home-indicator swipe zone, and duplicated the toolbar's
          "New Event" button at every width. Calendar.scss no longer describes it. */}
    </div>
  );
};

export default FirebaseCalendarComponent;
